import { useState, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { FaDownload, FaPrint, FaTrash } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Papa from 'papaparse';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorText from '../../components/ErrorText';
import OrdersTable from '../../components/admin/OrdersTable';
import ActivationsTable, {
  activationAudienceName,
  activationDestination,
} from '../../components/admin/ActivationsTable';
import Tab from '../../components/Tab';
import {
  ALL_ORDERS_QUERY,
  ALL_ACTIVATIONS_QUERY,
} from '../../queries/adminQueries';
import { useActionModal } from '../../hooks/useActionModal';
import Button from '../../components/inputs/Button';
import DatePickerInput from '../../components/inputs/DatePickerInput';
import { formatDateCellCustom } from '../../helpers/reactTable';
import { extractVariablesFromAudienceFilters } from '../../helpers/audienceFilters';

// Re-exported: AdminOrders.test.ts and the activations table both reach for
// this, and moving it out from under the test's feet buys nothing.
export { extractVariablesFromAudienceFilters };

/**
 * Is an epoch-millisecond timestamp inside the page's date range?
 *
 * Both tabs share one date picker, so both run the same predicate over their
 * own date field. Bounds are inclusive to the whole day at each end — picking
 * the same day for start and end shows that day, which is what the control
 * implies.
 *
 * Exported for the unit test; this file's convention (see above).
 */
export const isWithinDateRange = (
  msValue: string | number | null | undefined,
  startDate: Date | null,
  endDate: Date | null
): boolean => {
  if (!startDate && !endDate) return true;
  if (!msValue) return false;

  const value = new Date(Number(msValue));
  if (Number.isNaN(value.getTime())) return false;

  const start = startDate
    ? new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
    : null;
  const end = endDate
    ? new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate(),
        23,
        59,
        59
      )
    : null;

  if (start && value < start) return false;
  if (end && value > end) return false;
  return true;
};

/**
 * CSV rows for the activations tab (ATAD-132).
 *
 * Uses the same accessors the table renders from, so an export can never
 * disagree with what was on screen. Carries no s3Config / sftpConfig: those
 * hold plaintext credentials and have no business in a downloadable file.
 *
 * percentSelected, recordsSuccessful and recordsFailed are omitted here for
 * the same reason they are omitted from the table — the first is never
 * applied to the delivered file, and the other two are copied from
 * recordsIncluded rather than measured.
 */
export const buildActivationCsvRows = (activations) =>
  activations.map((activation) => ({
    'Activation ID': activation.id,
    'Activation Date': formatDateCellCustom({
      value: activation.createdAt,
      ifEmpty: 'N/A',
      pattern: 'MM/dd/yyyy HH:mm:ss',
    }),
    'User Email': activation.user?.email || 'N/A',
    Company: activation.user?.companyName || 'N/A',
    Audience: activationAudienceName(activation),
    'Audience Size': activation.audience?.recordsIncluded ?? 'N/A',
    'Records Activated': activation.recordsIncluded ?? 'N/A',
    Destination: activationDestination(activation),
    Folder: activation.destinationPartner?.displayName || 'N/A',
    'Delivered Bucket': activation.deliveredBucket || 'N/A',
    'Delivered File': activation.deliveredKey || 'N/A',
    'Delivered At': formatDateCellCustom({
      value: activation.deliveredAt,
      ifEmpty: 'N/A',
      pattern: 'MM/dd/yyyy HH:mm:ss',
    }),
    Status: activation.processingStatus || 'N/A',
    'Finished At': formatDateCellCustom({
      value: activation.finishedAt,
      ifEmpty: 'N/A',
      pattern: 'MM/dd/yyyy HH:mm:ss',
    }),
  }));

// PR 2d: audience-flavored order types all carry filters. Covers
// `audience`, `audience-enhancement`, `audience-profile`.
const AUDIENCE_ORDER_TYPES = new Set([
  'audience',
  'audience-enhancement',
  'audience-profile',
]);

// Helper to format variables string for CSV
const formatVariablesForCSV = (order: any): string => {
  if (AUDIENCE_ORDER_TYPES.has(order.orderType)) {
    // For audience orders, parse filters. order.audienceFilters is the
    // server-resolved field that prefers the snapshot over live filters
    // (per the admin allOrders resolver), so this works correctly even
    // when the source Audience has been edited/deleted.
    const { included, excluded } = extractVariablesFromAudienceFilters(order.audienceFilters);

    const parts: string[] = [];
    if (included.length > 0) {
      parts.push(`Included: ${included.join(', ')}`);
    }
    if (excluded.length > 0) {
      parts.push(`Excluded: ${excluded.join(', ')}`);
    }

    return parts.length > 0 ? parts.join(' | ') : 'N/A';
  } else if (order.orderType === 'enhancement' || order.orderType === 'insight') {
    // For enhancement/insight orders, use packageVariables
    if (order.packageVariables && order.packageVariables.length > 0) {
      return `Included: ${order.packageVariables.join(', ')}`;
    }
    return 'N/A';
  }

  return 'N/A';
};

// PR 2d: format the snapshot's `capturedAt` ISO string for the CSV cell.
// Falls back to 'N/A' when there's no snapshot (legacy orders).
const formatSnapshotCapturedAt = (order: any): string => {
  const captured = order.audienceSnapshot?.capturedAt;
  if (!captured) return 'N/A';
  try {
    return new Date(captured).toISOString();
  } catch {
    return 'N/A';
  }
};

export default function AdminOrders() {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activeTab, setActiveTab] = useState<'orders' | 'activations'>('orders');
  const tableRef = useRef(null);

  const { loading, error, data } = useQuery(ALL_ORDERS_QUERY);
  const orders = data?.allOrders;

  // ATAD-132: activations load on first visit to their tab, so the page's
  // initial paint costs exactly what it did before this tab existed.
  const {
    loading: activationsLoading,
    error: activationsError,
    data: activationsData,
  } = useQuery(ALL_ACTIVATIONS_QUERY, { skip: activeTab !== 'activations' });
  const activations = activationsData?.allActivations;

  const { state: actionState, dispatch: dispatchAction } = useActionModal();

  const filteredOrders = orders?.filter((order) =>
    isWithinDateRange(order.createdDate, startDate, endDate)
  );

  // Filtered on createdAt, not startedAt: activations created by the retired
  // checkout flow never got a startedAt, so it is the only date every row has.
  const filteredActivations = activations?.filter((activation) =>
    isWithinDateRange(activation.createdAt, startDate, endDate)
  );

  const handleDownload = () => {
    const isActivations = activeTab === 'activations';
    const rowsToDownload = isActivations
      ? filteredActivations || activations || []
      : filteredOrders || orders || [];

    if (rowsToDownload.length === 0) {
      alert(`No ${isActivations ? 'activations' : 'orders'} to download`);
      return;
    }

    const csvData = isActivations
      ? buildActivationCsvRows(rowsToDownload)
      : buildOrderCsvRows(rowsToDownload);

    // Generate CSV content
    const csv = Papa.unparse(csvData);

    // Create and trigger download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);

    // Generate filename with current date and filter info
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD format
    const isFiltered = (startDate || endDate) ? '_filtered' : '';
    const filename = `${isActivations ? 'activations' : 'orders'}_export${isFiltered}_${dateStr}.csv`;

    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const buildOrderCsvRows = (ordersToDownload) =>
    ordersToDownload.map(order => ({
      'Order ID': order.id,
      'User Email': order.user?.email || 'N/A',
      'Payment Type': getPaymentType(order),
      'Order Date': formatDateCellCustom({
        value: order.createdDate,
        pattern: 'MM/dd/yyyy HH:mm:ss'
      }),
      'Order Name': getOrderName(order),
      'Order Type': getOrderType(order.orderType),
      'Amount': order.amount ? `$${order.amount}` : '$0',
      'Records Included': order.recordsIncluded || 0,
      'Audience Name': order.audienceName || 'N/A',
      'Seed File Name': order.seedFileName || 'N/A',
      'Package Name': order.packageName || 'N/A',
      'Variables Included': formatVariablesForCSV(order),
      // PR 2d: snapshot of the audience as it was at order time. Empty for
      // legacy orders (audienceSnapshot absent) and for non-audience order
      // types. Lets us reproduce / debug the audience even if the source
      // Audience doc has been edited or deleted.
      'Audience Summary': order.audienceSnapshot?.plainText || 'N/A',
      'Snapshot Captured At': formatSnapshotCapturedAt(order),
      'Payment Intent ID': order.paymentIntentId || 'N/A',
      'Invoice ID': order.invoiceId || 'N/A'
    }));

  // Helper functions (matching OrdersTable.tsx)
  const getPaymentType = (order) => {
    if (order.paymentMethod?.brand && order.paymentMethod?.last4) {
      return `${order.paymentMethod.brand.charAt(0).toUpperCase() + order.paymentMethod.brand.slice(1)} ****${order.paymentMethod.last4}`;
    }
    if (order.user?.paymentType === 'invoice') {
      return 'Invoice';
    }
    return 'Stripe';
  };

  const getOrderName = (order) => {
    if (order.audienceName) return order.audienceName;
    if (order.seedFileName && order.packageName) {
      return `${order.seedFileName} (${order.packageName})`;
    }
    return order.seedFileName || order.packageName || 'N/A';
  };

  const getOrderType = (orderType) => {
    if (!orderType) return 'N/A';
    return orderType.charAt(0).toUpperCase() + orderType.slice(1);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleClearAllFilters = () => {
    if (tableRef.current && tableRef.current.setAllFilters) {
      tableRef.current.setAllFilters([]);
    }
    // Also clear date filters
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <div className="flex flex-col w-full">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-4">Orders</h1>

        {/* ATAD-132: activations live behind a tab rather than in the orders
            table — they describe deliveries, not purchases, and share almost
            no columns. No role check here: /admin/orders is already in
            AdminRoutes' FINANCE_ADMIN_PATHS, so the tab inherits the route's
            gating, and the server guards allActivations with anyAdminProtected. */}
        <div className="flex items-center border-b border-gray-200 mb-4">
          <Tab
            label="Orders"
            selected={activeTab === 'orders'}
            onClick={() => setActiveTab('orders')}
          />
          <div className="w-px h-5 bg-gray-200" />
          <Tab
            label="Activations"
            selected={activeTab === 'activations'}
            onClick={() => setActiveTab('activations')}
          />
        </div>

        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <label htmlFor="startDate" className="text-sm font-medium whitespace-nowrap">Start Date</label>
            <DatePicker
              id="startDate"
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              dateFormat="MM/dd/yyyy"
              placeholderText="mm/dd/yyyy"
              className="border border-gray-300 rounded px-3 py-2 text-sm w-32 focus:ring-0 focus:border-gray-400"
            />
          </div>
          <div className="flex items-center space-x-2">
            <label htmlFor="endDate" className="text-sm font-medium whitespace-nowrap">End Date</label>
            <DatePicker
              id="endDate"
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              dateFormat="MM/dd/yyyy"
              placeholderText="mm/dd/yyyy"
              className="border border-gray-300 rounded px-3 py-2 text-sm w-32 focus:ring-0 focus:border-gray-400"
            />
          </div>
          <Button
            type="button"
            primary
            small
            onClick={handleDownload}
            className="flex items-center space-x-2"
          >
            <FaDownload size={14} />
            <span>Download</span>
          </Button>
          <Button
            type="button"
            small
            onClick={handlePrint}
            className="flex items-center space-x-2"
          >
            <FaPrint size={14} />
            <span>Print</span>
          </Button>
          <Button
            type="button"
            small
            onClick={handleClearAllFilters}
            className="flex items-center space-x-2"
          >
            <FaTrash size={14} />
            <span>Clear Filters</span>
          </Button>
        </div>
      </div>

      {/* Only the active tab is mounted, so `tableRef` always points at the
          table on screen and Clear Filters needs no second ref. Trade-off:
          per-column filters reset when you switch tabs. */}
      <div className="w-full">
        {activeTab === 'orders' ? (
          <>
            {loading && <LoadingSpinner message="Loading orders..." />}
            {error && <ErrorText className="mt-2" message={error.message} />}
            {filteredOrders && filteredOrders?.length > 0 && (
              <OrdersTable
                ref={tableRef}
                orders={filteredOrders}
                ordersQuery={ALL_ORDERS_QUERY}
                actionState={actionState}
                dispatchAction={dispatchAction}
              />
            )}
            {filteredOrders?.length === 0 && <p>No orders found for the selected date range.</p>}
          </>
        ) : (
          <>
            {activationsLoading && (
              <LoadingSpinner message="Loading activations..." />
            )}
            {activationsError && (
              <ErrorText className="mt-2" message={activationsError.message} />
            )}
            {filteredActivations && filteredActivations.length > 0 && (
              <ActivationsTable
                ref={tableRef}
                activations={filteredActivations}
              />
            )}
            {filteredActivations?.length === 0 && (
              <p>No activations found for the selected date range.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
